import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { toast } from "sonner";
import { isValidUrl } from "@/zapi-frontend/shared/utils/url";

type TAddWebLinkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => void;
  title?: string;
  submitLabel?: string;
  cancelLabel?: string;
};

export function AddWebLinkModal({
  isOpen,
  onClose,
  onSubmit,
  title = "Add Web Link",
  submitLabel = "Add",
  cancelLabel = "Cancel"
}: TAddWebLinkModalProps) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!isOpen) setUrl("");
  }, [isOpen]);

  const handleSubmit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;

    const normalized =
      trimmed.startsWith("http://") || trimmed.startsWith("https://") ? trimmed : `https://${trimmed}`;

    if (!isValidUrl(normalized)) {
      toast.error("Please enter a valid URL");
      return;
    }

    onSubmit(normalized);
    setUrl("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="w-[420px]">
        <DialogHeader>
          <DialogTitle>
            <FigmaTypography variant="heading-3" color="zinc900">
              {title}
            </FigmaTypography>
          </DialogTitle>
        </DialogHeader>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (isEnterSubmit(e, { allowShift: true })) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="https://example.com"
          className="w-full h-9 px-3 border border-innoveraZinc-150 rounded-md text-[14px] outline-none focus:border-innoveraZinc-400"
        />
        <DialogFooter>
          <AppButton appearance="outlined" color="zinc150" onClick={onClose}>
            {cancelLabel}
          </AppButton>
          <AppButton onClick={handleSubmit} disabled={!url.trim()}>
            {submitLabel}
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
